import {useQuery} from '@apollo/client';
import {FinancialCurrencyCard} from './FinancialCards';
import {GET_REVENUE_METRICS} from 'graphql/queries/financial';
import {Form, Alert} from 'antd';
import {useState, useMemo} from 'react';
import styles from './financial.module.scss';
import {MetricsFilterBar} from './MetricsFilterBar';
import {MetricsEmpty} from './MetricsEmpty';
import moment from 'moment';
import {ExportFinancialButton} from './ExportFinancialButton';
import {currentDayOfMonth, firstDayOfMonth} from './WalletMetrics';
import {NavTabsEnum} from 'utils/constants';
import {useSelector} from 'react-redux';
import {makeSelectTabOperations} from 'redux/store/auth';

// Helper function to group fees by type
const groupFeesByType = fees => {
  if (!fees || !Array.isArray(fees)) return {};

  return fees.reduce((acc, fee) => {
    const feeType = fee.feeType || 'Unknown';
    if (!acc[feeType]) {
      acc[feeType] = [];
    }
    acc[feeType].push(fee);
    return acc;
  }, {});
};

// Helper function to group fees by currency
const groupFeesByCurrency = fees => {
  if (!fees || !Array.isArray(fees)) return {};

  return fees.reduce((acc, fee) => {
    const currency = fee.currency || 'Unknown';
    if (!acc[currency]) {
      acc[currency] = [];
    }
    acc[currency].push(fee);
    return acc;
  }, {});
};

// Helper function to render loading skeleton
const renderLoadingSkeleton = (count = 3) =>
  Array(count)
    .fill('')
    .map((_, key) => (
      <div key={key} className={styles.loadingSkeleton}>
        {_}
      </div>
    ));

// Helper function to render currency cards
const renderCurrencyCards = (data, loading, typeField = null) => {
  if (loading) {
    return renderLoadingSkeleton();
  }

  if (!data || !Array.isArray(data) || data.length === 0) {
    return <MetricsEmpty />;
  }

  return data.map((item, key) => (
    <FinancialCurrencyCard
      type={item?.currency}
      key={key}
      currency={item?.currency}
      symbol={item?.symbol}
      amount={item?.amount}
      feeType={typeField ? item?.[typeField] : null}
    />
  ));
};

// Helper function to render fee type cards
const renderFeeTypeCards = (fees, loading) => {
  if (loading) {
    return renderLoadingSkeleton();
  }

  if (!fees || !Array.isArray(fees) || fees.length === 0) {
    return <MetricsEmpty>No fees recorded for this period</MetricsEmpty>;
  }

  const groupedByType = groupFeesByType(fees);

  return (
    <div className={styles.feeBreakdown}>
      {Object.entries(groupedByType).map(([feeType, feeItems]) => {
        // Group by currency for each fee type
        const groupedByCurrency = groupFeesByCurrency(feeItems);

        return (
          <div key={feeType} className={styles.feeTypeSection}>
            <h3 className={styles.feeTypeTitle}>{feeType}</h3>
            <div className={styles.cards}>
              {Object.entries(groupedByCurrency).map(
                ([currency, currencyFees]) => {
                  const totalAmount = currencyFees.reduce(
                    (sum, fee) => sum + (fee.amount || 0),
                    0,
                  );
                  const firstFee = currencyFees[0];

                  return (
                    <FinancialCurrencyCard
                      type={firstFee?.currency}
                      key={`${feeType}-${currency}`}
                      currency={firstFee?.currency}
                      symbol={firstFee?.symbol}
                      amount={totalAmount}
                      feeType={feeType}
                    />
                  );
                },
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const RevenueMetrics = () => {
  const [form] = Form.useForm();
  const startDate = Form.useWatch('startDate', form);
  const endDate = Form.useWatch('endDate', form);
  const allTime = Form.useWatch('allTime', form);
  const status = Form.useWatch('status', form);

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.Financials),
  );

  const [data, setData] = useState({});
  const {loading, error, refetch} = useQuery(GET_REVENUE_METRICS, {
    variables: {
      filters: {
        date: {
          range: 'MONTHLY',
        },
      },
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => setData(data?.getRevenueMetrics?.data),
  });

  // Memoized computed values
  const hasData = useMemo(() => {
    return (
      data &&
      Object.keys(data).length > 0 &&
      Object.values(data).some(
        value => Array.isArray(value) && value.length > 0,
      )
    );
  }, [data]);

  return (
    <div className={styles.revenueMetricsContainer}>
      {/* Filters Section */}
      <MetricsFilterBar
        form={form}
        loading={loading}
        initialValues={{
          startDate: firstDayOfMonth,
          endDate: currentDayOfMonth,
        }}
        actions={
          tabOperations?.export ? (
            <ExportFinancialButton
              startDate={startDate}
              endDate={endDate}
              allTime={allTime}
              status={status}
              query={GET_REVENUE_METRICS}
              type="Order"
            />
          ) : null
        }
        onFinish={values => {
          if (values?.allTime) {
            return refetch({
              'filters': {
                'date': {
                  'range': 'MONTHLY',
                },
                ...(values?.status && {status: values?.status}),
                'allTime': true,
              },
            });
          }
          if (!values?.startDate && !values?.endDate) {
            return refetch({
              filters: {
                ...(values?.status && {status: values?.status}),
                date: {
                  range: 'MONTHLY',
                },
              },
            });
          }
          refetch({
            filters: {
              ...(values?.status && {status: values?.status}),
              date: {
                ...(values?.startDate && {
                  startDate: moment(values?.startDate).format(),
                }),
                ...(values?.endDate && {
                  endDate: moment(values?.endDate).format(),
                }),
              },
            },
          });
        }}
      />

      {/* Error Display */}
      {error && (
        <Alert
          message="Error"
          description="Failed to load revenue metrics. Please try again."
          type="error"
          showIcon
          className={styles.errorAlert}
        />
      )}

      {/* Main Metric Cards Row */}
      <div className={styles.metricCardsRow}>
        <div className={styles.metricCardBox}>
          <h2 className={styles.sectionTitle}>Total Transactional Revenue</h2>
          <div className={styles.metricCardList}>
            {renderCurrencyCards(data?.totalRevenue, loading)}
          </div>
        </div>
        <div className={styles.metricCardBox}>
          <h2 className={styles.sectionTitle}>
            Total Daba Pro Subscription Fees
          </h2>
          <div className={styles.metricCardList}>
            {renderCurrencyCards(data?.totalDabaProSubscriptionFees, loading)}
          </div>
        </div>
        <div className={styles.metricCardBox}>
          <h2 className={styles.sectionTitle}>Daba Academy Subscriptions</h2>
          <div className={styles.metricCardList}>
            {renderCurrencyCards(
              data?.totalDabaAcademySubscriptions,
              loading,
              'type',
            )}
          </div>
        </div>
        <div className={styles.metricCardBox}>
          <h2 className={styles.sectionTitle}>Simulator Subscriptions</h2>
          <div className={styles.metricCardList}>
            {renderCurrencyCards(
              data?.totalSimulatorSubscriptions,
              loading,
              'type',
            )}
          </div>
        </div>
      </div>

      {/* Metrics Display */}
      <div className={styles.metricsContainer}>
        {/* Order Fees Section - Grouped by Fee Type */}
        <div className={styles.metricSection}>
          <h2 className={styles.sectionTitle}>Order Fees Breakdown</h2>
          {renderFeeTypeCards(data?.totalOrderFees, loading)}
        </div>

        {/* Daba Pro Order Fees Section - Grouped by Fee Type */}
        <div className={styles.metricSection}>
          <h2 className={styles.sectionTitle}>Daba Pro Order Fees Breakdown</h2>
          {renderFeeTypeCards(data?.totalOrderDabaProFees, loading)}
        </div>

        {/* Empty State */}
        {!loading && !hasData && !error && (
          <div className={styles.emptyStateContainer}>
            No revenue metrics for the filters you have applied.
          </div>
        )}
      </div>
    </div>
  );
};
